/**
 * Shared particle state shape.
 *
 * Kept in its own file because both `ParticleCanvas` (the runtime) and
 * the per-effect renderers (`effects/*.ts`) need to share types.
 */

export type CanvasEffectId =
  | 'floating-heart-bokeh'
  | 'rose-petal-cascade'
  | 'sparkler-trail-hearts'
  | 'constellation-connection'
  | 'velvet-dust-motes'
  | 'heartbeat-pulse-rings'
  | 'champagne-bubble-rise'
  | 'firefly-courtship'
  | 'silk-ribbon-flow'
  | 'diamond-dust-sparkle'
  | 'pheromone-trail'
  | 'aurora-heart'
  | 'floating-kiss-marks'
  | 'memory-fragment-drift'
  | 'entwined-particle-vines';

export type EffectId = CanvasEffectId;

export type PointerSample = {
  x: number;
  y: number;
  active: boolean;
  time: number;
};

export type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  color: string;
  life: number;
  maxLife: number;
  phase: number;
  rot: number;
  vr: number;
  seed: number;
  scale: number;
  baseX: number;
  baseY: number;
  blur: number;
  depth: number;
  side: number;
  settled: number;
};

export type Ribbon = {
  points: Particle[];
  colorA: string;
  colorB: string;
  phase: number;
  width: number;
};

export type Fragment = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  seed: number;
  color: string;
};

export type CanvasState = {
  particles: Particle[];
  sparks: Particle[];
  blooms: Particle[];
  ribbons: Ribbon[];
  fragments: Fragment[];
  trailCanvas: HTMLCanvasElement | null;
  trailCtx: CanvasRenderingContext2D | null;
  width: number;
  height: number;
  dpr: number;
  lastSpawn: number;
};

export function makeParticle(values: Partial<Particle>): Particle {
  return {
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    size: 1,
    alpha: 1,
    color: '#ffffff',
    life: 1,
    maxLife: 1,
    phase: 0,
    rot: 0,
    vr: 0,
    seed: Math.random() * 1000,
    scale: 1,
    baseX: 0,
    baseY: 0,
    blur: 0,
    depth: 0,
    side: 0,
    settled: 0,
    ...values,
  };
}

export function createEmptyCanvasState(): CanvasState {
  return {
    particles: [],
    sparks: [],
    blooms: [],
    ribbons: [],
    fragments: [],
    trailCanvas: null,
    trailCtx: null,
    width: 1,
    height: 1,
    dpr: 1,
    lastSpawn: 0,
  };
}
